import { exists, window } from 'browser-monads-ts'

import { ensureArray, ensureString, objectEmpty } from './display'

export class WebCrypto {
  constructor({ crypto: _crypto, util: _util } = {}) {
    this._crypto = _crypto ?? {}
    this._util = _util ?? {}
  }

  get subtle() {
    return this._crypto?.subtle
  }

  async getWebCrypto() {
    if (!this._crypto?.subtle) {
      try {
        this._crypto = (await import('isomorphic-webcrypto')).default
      } catch (_ignore) {
        console.info('Failed to import isomorphic-webcrypto, retrying w/ node crypto')
        try {
          this._crypto = (await import('crypto')).default
        } catch (error) {
          // eslint-disable-next-line max-len
          console.error(`Failed to import node crypto, ensure 'isomorphic-webcrypto' (or node 'crypto') is installed and/or pass in implementation via 'new WebCrypto({ crypto })'`)
          throw error
        }
      }
    }

    if (!this._crypto?.subtle) {
      throw new Error('Invalid crypto, missing subtle')
    }

    return this._crypto
  }

  async getTextDecoder() {
    if (this._util?.TextDecoder) {
      return this._util.TextDecoder
    }

    if (exists(window) && typeof window?.TextDecoder === 'function') {
      return window.TextDecoder
    }

    try {
      const TextDecoder = (await import('util')).TextDecoder
      this._util.TextDecoder = TextDecoder

      return TextDecoder
    } catch (error) {
      console.error(`Failed to import 'utils.TextDecoder', ensure 'util' is available and/or pass in implementation via 'new WebCrypto({ util })'`)
      throw error
    }
  }

  async getTextEncoder() {
    if (this._util?.TextEncoder) {
      return this._util.TextEncoder
    }

    if (exists(window) && typeof window?.TextEncoder === 'function') {
      return window.TextEncoder
    }

    try {
      const TextEncoder = (await import('util')).TextEncoder
      this._util.TextEncoder = TextEncoder

      return TextEncoder
    } catch (error) {
      console.error(`Failed to import 'utils.TextEncoder', ensure 'util' is available and/or pass in implementation via 'new WebCrypto({ util })'`)
      throw error
    }
  }

  timingSafeEqual(value1, value2) {
    if (
      (value1 == undefined) ||
      (value2 == undefined) ||
      (value1.length !== value2.length)
    ) {
      return false
    }

    let result = 0
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < value1.length; i++) {
      // eslint-disable-next-line no-bitwise
      result |= value1[i] ^ value2[i]
    }

    return (result === 0)
  }

  stringToHex(stringValue) {
    return (
      Array.from(ensureString(stringValue), (char) => (
        char.charCodeAt(0).toString(16).padStart(2, '0')
      )).join('')
    )
  }

  hexToString(hexValue) {
    return (
      ensureArray(
        ensureString(hexValue).match(/.{1,2}/g)
      )
      .map((byte) => String.fromCharCode(parseInt(byte, 16)))
      .join('')
    )
  }

  async arrayBufferToString(arrayBuffer) {
    const uint8Array = new Uint8Array(arrayBuffer)
    const Decoder = await this.getTextDecoder()
    return new Decoder().decode(uint8Array)
  }

  arrayToArrayBuffer(array) {
    return (
      (ArrayBuffer.from != undefined)
        ? ArrayBuffer.from(array)
        : new Uint8Array(array).buffer
    )
  }

  ensureArrayBuffer(arrayOrArrayBuffer) {
    return (
      (arrayOrArrayBuffer instanceof ArrayBuffer)
        ? arrayOrArrayBuffer
        : this.arrayToArrayBuffer(arrayOrArrayBuffer)
    )
  }

  getKeyOperations(keyType) {
    switch (keyType) {
      case 'paired':
      case 'private':
      case 'privateKey': {
        return ['deriveKey']
      }

      case 'secret':
      case 'secretKey':
      case 'sharedSecret': {
        return ['encrypt', 'decrypt']
      }

      default: {
        return []
      }
    }
  }

  getKeyAlgorythm(keyType) {
    switch (keyType) {
      case 'derivedKey':
      case 'derived':
      case 'secret':
      case 'secretKey':
      case 'sharedSecret': {
        return {
          name: 'AES-GCM',
        }
      }

      default: {
        return {
          name: 'ECDH',
          namedCurve: 'P-256',
        }
      }
    }
  }

  async generateKeyPair() {
    const crypto = await this.getWebCrypto()
    const keyPair = await crypto.subtle.generateKey(
      this.getKeyAlgorythm('paired'),
      true,
      this.getKeyOperations('paired')
    )

    return keyPair
  }

  async generateSharedSecret() {
    const crypto = await this.getWebCrypto()
    const keyPair = await crypto.subtle.generateKey(
      this.getKeyAlgorythm('sharedSecret'),
      true,
      this.getKeyOperations('sharedSecret')
    )

    return keyPair
  }

  async generateHMAC(message, derivedKey) {
    if (!message || !derivedKey) {
      return undefined
    } 

    const crypto = await this.getWebCrypto()
    const Encoder = await this.getTextEncoder()

    const signature = await crypto.subtle.sign(
      'HMAC',
      derivedKey,
      new Encoder().encode(message)
    )

    return this.stringToHex(
      this.arrayBufferToString(signature)
    )
  }

  async verifyHMAC(message, derivedKey, verifiableHMAC) {
    const calculatedHMAC = await this.generateHMAC(message, derivedKey)

    return this.timingSafeEqual(calculatedHMAC, verifiableHMAC)
  }

  async getStorableKey(key) {
    const crypto = await this.getWebCrypto()

    const exportedJWK = await crypto.subtle.exportKey('jwk', key)
    return this.stringToHex(JSON.stringify(exportedJWK))
  }

  async restoreStorableKey(keyType, storableHex) {
    if (!storableHex) {
      return undefined
    }
    const crypto = await this.getWebCrypto()

    const exportedJWK = JSON.parse(this.hexToString(storableHex) || '{}')
    if (objectEmpty(exportedJWK)) {
      return undefined
    }

    return crypto.subtle.importKey(
      'jwk',
      exportedJWK,
      this.getKeyAlgorythm(keyType),
      true,
      this.getKeyOperations(keyType)
    )
  }

  async getStorableKeyPair(keyPair) {
    const storableKeys = {}

    // eslint-disable-next-line no-restricted-syntax
    for (const [keyType, key] of Object.entries(keyPair)) {
      // eslint-disable-next-line no-await-in-loop
      storableKeys[keyType] = await this.getStorableKey(key)
    }

    return storableKeys
  }

  async restoreStorableKeyPair(keyPair) {
    const restoredKeys = {}

    // eslint-disable-next-line no-restricted-syntax
    for (const [keyType, key] of Object.entries(keyPair)) {
      // eslint-disable-next-line no-await-in-loop
      restoredKeys[keyType] = await this.restoreStorableKey(keyType, key)
    }

    return restoredKeys
  }

  async deriveSharedKey({ publicKey, privateKey }) {
    if (!publicKey || !privateKey) {
      return undefined
    }

    const crypto = await this.getWebCrypto()
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'ECDH',
        public: publicKey,
      },
      privateKey,
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    )
    return derivedKey
  }

  async deriveHMACKey({ publicKey, privateKey }) {
    if (!publicKey || !privateKey) {
      return undefined
    }

    const crypto = await this.getWebCrypto()
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'ECDH',
        public: publicKey,
      },
      privateKey,
      {
        name: 'HMAC',
        hash: { name: 'SHA-256' },
        length: 256,  // Adjusted key length, e.g., 128 bits
      },
      true,
      ['sign', 'verify']
    )
    return derivedKey
  }

  async getVerificationKeys({ publicKey, privateKey }) {
    if (!publicKey || !privateKey) {
      return {}
    }

    const sharedKeyPair = await this.restoreStorableKeyPair({ publicKey, privateKey })
    const derivedHMACKey = await this.deriveHMACKey(sharedKeyPair)
    const derivedSecretKey = await this.deriveSharedKey(sharedKeyPair)

    return {
      derivedSecretKey,
      derivedHMACKey,
    }
  }

  async encryptMessage(decryptedMessage, derivedKey) {
    if (!decryptedMessage || !derivedKey) {
      return undefined
    }

    const crypto = await this.getWebCrypto()
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const Encoder = await this.getTextEncoder()
    const encodedMessage = new Encoder().encode(decryptedMessage)
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      derivedKey,
      encodedMessage
    )

    const encryptedMessage = new Uint8Array([
      ...iv,
      ...new Uint8Array(ciphertext)
    ])
    return Array.from(encryptedMessage)
  }

  async decryptMessage(encryptedMessage, derivedKey) {
    if (!encryptedMessage || !derivedKey) {
      return undefined
    }

    const crypto = await this.getWebCrypto()
    // NOTE: this presumed an array or arrayBuffer coming in as encryptedMessage (will fail w/ IV error if its a string)
    const encryptedArrayBuffer = this.ensureArrayBuffer(encryptedMessage)
    const iv = encryptedArrayBuffer.slice(0, 12)
    const ciphertext = encryptedArrayBuffer.slice(12)

    const decryptedArrayBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      derivedKey,
      ciphertext
    )

    const Decoder = await this.getTextDecoder()
    const decryptedMessage = new Decoder().decode(decryptedArrayBuffer)
    return decryptedMessage
  }
}
